import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { BookRecommendation } from "./useBookRecommendations";
import { toast } from "sonner";

// Constants
const DAILY_REFRESH_LIMIT = 3;
const LOW_CACHE_THRESHOLD = 15;
const TARGET_CACHE_COUNT = 25;

// Normalize title for comparison
const normalizeTitle = (title: string) => 
  title.toLowerCase().replace(/[^a-z0-9]/g, '').trim();

// Check if title is a box set or collection
const isBoxSetOrCollection = (title: string) => {
  const t = (title || '').toLowerCase();
  return t.includes('box set') || 
         t.includes('boxed set') ||
         t.includes('books set') ||
         t.includes('book set') ||
         t.match(/set of \d+ books/i) !== null ||
         t.match(/\(\d+ books?\)/i) !== null ||
         t.match(/\d+ books? (set|collection|series)/i) !== null ||
         t.match(/complete (series|collection|set)/i) !== null ||
         t.match(/series collection/i) !== null ||
         t.match(/series set/i) !== null;
};

// Known series patterns for filtering out series user has already read
const KNOWN_SERIES_NAMES = [
  'throne of glass', 'court of thorns', 'crescent city', 'acotar', 
  'a court of', 'harry potter', 'hunger games', 'divergent',
  'twilight', 'percy jackson', 'game of thrones', 'lord of the rings',
  'shadow and bone', 'six of crows', 'red queen', 'the selection',
  'from blood and ash', 'shatter me', 'the cruel prince',
  'iron fey', 'mortal instruments', 'infernal devices', 'dark artifices',
  'fourth wing', 'house of earth', 'house of sky', 'house of flame',
  'bridgerton', 'outlander', 'red rising', 'mistborn', 'stormlight',
  'wheel of time', 'first law', 'gentlemen bastard', 'kingkiller',
  'dune', 'foundation', 'expanse', 'ender', 'dark tower'
];

interface CachedRecommendation {
  id: string;
  book_id: string | null;
  book_title: string;
  book_author: string | null;
  cover_url: string | null;
  reason: string | null;
  recommendation_type: string | null;
  source_books: any;
  confidence_score: number | null;
  match_details: string | null;
  status: string;
  created_at: string;
}

interface RefreshLimitRecord {
  user_id: string;
  refreshes_today: number;
  last_refresh_at: string | null;
  refresh_date: string;
  created_at: string;
}

export const useCachedRecommendations = (limit: number = 6) => {
  const queryClient = useQueryClient();
  const [refreshesRemaining, setRefreshesRemaining] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Check and get remaining refresh count
  const checkRefreshLimit = useCallback(async (): Promise<number> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;

    const today = new Date().toISOString().split('T')[0];

    // Get or create user's limit record
    let { data: limitRecord, error } = await supabase
      .from('user_refresh_limits')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (error && error.code === 'PGRST116') {
      // Record doesn't exist, create it
      const { data: newRecord } = await supabase
        .from('user_refresh_limits')
        .insert({ 
          user_id: user.id, 
          refreshes_today: 0, 
          refresh_date: today 
        })
        .select()
        .single();
      limitRecord = newRecord as RefreshLimitRecord | null;
    }

    if (!limitRecord) return DAILY_REFRESH_LIMIT;

    // Reset if new day
    if (limitRecord.refresh_date !== today) {
      await supabase
        .from('user_refresh_limits')
        .update({ refreshes_today: 0, refresh_date: today })
        .eq('user_id', user.id);
      return DAILY_REFRESH_LIMIT;
    }

    return Math.max(0, DAILY_REFRESH_LIMIT - (limitRecord.refreshes_today || 0));
  }, []);

  // Increment refresh count (called when manual refresh is triggered)
  const incrementRefreshCount = useCallback(async (): Promise<boolean> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const today = new Date().toISOString().split('T')[0];

    // Get current count
    const { data: limitRecord } = await supabase
      .from('user_refresh_limits')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (!limitRecord) {
      // Create new record with 1 refresh
      await supabase
        .from('user_refresh_limits')
        .insert({ 
          user_id: user.id, 
          refreshes_today: 1, 
          refresh_date: today,
          last_refresh_at: new Date().toISOString()
        });
      setRefreshesRemaining(DAILY_REFRESH_LIMIT - 1);
      return true;
    }

    // Check if it's a new day - reset counter
    if (limitRecord.refresh_date !== today) {
      await supabase
        .from('user_refresh_limits')
        .update({ 
          refreshes_today: 1, 
          refresh_date: today,
          last_refresh_at: new Date().toISOString()
        })
        .eq('user_id', user.id);
      setRefreshesRemaining(DAILY_REFRESH_LIMIT - 1);
      return true;
    }

    // Check if limit reached
    if ((limitRecord.refreshes_today || 0) >= DAILY_REFRESH_LIMIT) {
      setRefreshesRemaining(0);
      return false;
    }

    // Increment counter
    const newCount = (limitRecord.refreshes_today || 0) + 1;
    await supabase
      .from('user_refresh_limits')
      .update({ 
        refreshes_today: newCount,
        last_refresh_at: new Date().toISOString()
      })
      .eq('user_id', user.id);
    
    setRefreshesRemaining(DAILY_REFRESH_LIMIT - newCount);
    return true;
  }, []);

  const query = useQuery({
    queryKey: ['cachedRecommendations', limit],
    queryFn: async (): Promise<{ 
      recommendations: BookRecommendation[], 
      classicRecommendation: BookRecommendation | null,
      fromCache: boolean,
      cacheCount: number,
      needsMoreReviews?: boolean,
      reviewCount?: number,
      requiredCount?: number
    }> => {
      console.log('🔍 Fetching cached recommendations...');
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Check refresh limits on initial load
      const remaining = await checkRefreshLimit();
      setRefreshesRemaining(remaining);

      // Check if user has minimum reviews before fetching recommendations
      const { count: reviewCount } = await supabase
        .from('user_books')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', session.user.id)
        .not('rating_star', 'is', null)
        .gt('rating_star', 0);

      const MIN_REVIEWS = 3;
      if ((reviewCount || 0) < MIN_REVIEWS) {
        console.log(`⏸️ User has ${reviewCount || 0} reviews, need ${MIN_REVIEWS} for recommendations`);
        return {
          recommendations: [],
          classicRecommendation: null,
          fromCache: false,
          cacheCount: 0,
          needsMoreReviews: true,
          reviewCount: reviewCount || 0,
          requiredCount: MIN_REVIEWS
        };
      }

      // Fetch pending recommendations from cache
      const { data: cachedRecs, error: cacheError } = await supabase
        .from('user_cached_recommendations')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(limit + 5); // +5 for potential classic + safety margin

      if (cacheError) {
        console.error('❌ Cache query error:', cacheError);
        throw cacheError;
      }

      // Fetch current user library titles and detect series they've read
      const { data: tbrBooks } = await supabase
        .from('user_books')
        .select('books!user_books_book_id_fkey(title, series_name)')
        .eq('user_id', session.user.id)
        .in('shelf', ['to-read', 'reading', 'read', 'finished', 'paused', 'unfinished']);

      const excludedTitles = new Set(
        (tbrBooks || [])
          .map((ub: any) => normalizeTitle(ub.books?.title || ''))
          .filter(Boolean)
      );

      // Detect which series the user has read
      const userSeriesNames = new Set<string>();
      (tbrBooks || []).forEach((ub: any) => {
        const title = (ub.books?.title || '').toLowerCase();
        const seriesName = (ub.books?.series_name || '').toLowerCase();
        
        // Add explicit series name from DB
        if (seriesName) {
          userSeriesNames.add(seriesName);
        }
        
        // Also check known series patterns in titles
        for (const pattern of KNOWN_SERIES_NAMES) {
          if (title.includes(pattern)) {
            userSeriesNames.add(pattern);
          }
        }
      });

      console.log(`🛡️ Client-side safety net: ${excludedTitles.size} titles, ${userSeriesNames.size} series to exclude`);

      const cacheCount = cachedRecs?.length || 0;
      console.log(`📦 Found ${cacheCount} cached recommendations`);

      // If we have cached recs, use them
      if (cachedRecs && cachedRecs.length > 0) {
        const sessionId = crypto.randomUUID();
        
        // Check if rec is AI-generated (has source_books or personalized reason)
        const isAIGenerated = (rec: CachedRecommendation) => {
          if (rec.source_books && Array.isArray(rec.source_books) && rec.source_books.length > 0) return true;
          if (!rec.reason) return false;
          const genericPhrases = ['A popular book', 'stood the test of time', 'reader favorites'];
          return !genericPhrases.some(phrase => rec.reason?.includes(phrase));
        };
        
        // Sort: AI-generated first, then by date
        const sortedRecs = [...cachedRecs].sort((a, b) => {
          const aIsAI = isAIGenerated(a);
          const bIsAI = isAIGenerated(b);
          if (aIsAI && !bIsAI) return -1;
          if (!aIsAI && bIsAI) return 1;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        
        // Convert cached recs to BookRecommendation format
        // Filter out books already in library, box sets, trigger markers, and series user has read
        const recommendations = sortedRecs
          .filter((rec: CachedRecommendation) => {
            const normTitle = normalizeTitle(rec.book_title);
            const titleLower = (rec.book_title || '').toLowerCase();
            
            // Filter out trigger markers and system entries
            if (rec.recommendation_type === 'trigger_marker' || 
                rec.book_title?.includes('__GENERATION_TRIGGER__') ||
                rec.book_title?.includes('__') ||
                rec.book_title === 'Unknown') {
              console.log(`🛡️ Filtered (system marker): "${rec.book_title}"`);
              return false;
            }
            
            // Exact title match - already in library
            if (excludedTitles.has(normTitle)) {
              console.log(`🛡️ Filtered (already in library): "${rec.book_title}"`);
              return false;
            }
            
            // Box set filter
            if (isBoxSetOrCollection(rec.book_title)) {
              console.log(`🛡️ Filtered (box set): "${rec.book_title}"`);
              return false;
            }
            
            // Series filter - check if rec is from a series user has already read
            for (const seriesName of userSeriesNames) {
              if (titleLower.includes(seriesName)) {
                console.log(`🛡️ Filtered (read ${seriesName} series): "${rec.book_title}"`);
                return false;
              }
            }
            
            return true;
          })
          .map((rec: CachedRecommendation): BookRecommendation => ({
            title: rec.book_title,
            author: rec.book_author || 'Unknown',
            reason: rec.reason || '',
            bookId: rec.book_id || undefined,
            coverUrl: rec.cover_url || undefined,
            confidenceScore: rec.confidence_score || undefined,
            recommendationType: (rec.recommendation_type as BookRecommendation['recommendationType']) || 'safe_bet',
            sourceBooks: rec.source_books || [],
            matchDetails: rec.match_details || undefined,
            sessionId,
          }));

        // Separate classic from regular - prefer AI-generated classics
        const classicRec = recommendations.find(r => r.recommendationType === 'classic' && r.sourceBooks?.length > 0) 
          || recommendations.find(r => r.recommendationType === 'classic');
        const regularRecs = recommendations.filter(r => r.recommendationType !== 'classic').slice(0, limit - 1);

        // Mark these as shown (async, don't await)
        const shownIds = cachedRecs.slice(0, regularRecs.length + (classicRec ? 1 : 0)).map(r => r.id);
        supabase
          .from('user_cached_recommendations')
          .update({ status: 'shown', shown_at: new Date().toISOString() })
          .in('id', shownIds)
          .then(({ error }) => {
            if (error) console.error('Failed to mark as shown:', error);
          });

        // Trigger background generation if running low
        const remainingCount = cacheCount - shownIds.length;
        if (remainingCount < LOW_CACHE_THRESHOLD) {
          console.log(`⚠️ Low cache (${remainingCount} remaining), triggering background generation`);
          triggerBackgroundGeneration(session.access_token, 'low_cache');
        }

        return {
          recommendations: regularRecs,
          classicRecommendation: classicRec || null,
          fromCache: true,
          cacheCount: remainingCount
        };
      }

      // No cached recs - trigger generation with retry logic
      console.log('📭 No cached recommendations, triggering generation with retry...');
      
      // Trigger generation and poll for results
      const generationPromise = triggerBackgroundGeneration(session.access_token, 'empty_cache');
      
      // Wait for generation to complete (with timeout)
      try {
        await Promise.race([
          generationPromise,
          new Promise((_, reject) => setTimeout(() => reject(new Error('Generation timeout')), 15000))
        ]);
        
        // Small delay to let cache populate
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Retry fetching from cache
        const { data: retryRecs } = await supabase
          .from('user_cached_recommendations')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('status', 'pending')
          .gt('expires_at', new Date().toISOString())
          .order('created_at', { ascending: false })
          .limit(limit + 5);

        if (retryRecs && retryRecs.length > 0) {
          console.log(`✅ Retry successful: found ${retryRecs.length} cached recommendations`);
          
          // Process the retry results (simplified - just return basic data)
          const sessionId = crypto.randomUUID();
          const recommendations = retryRecs
            .filter((rec: CachedRecommendation) => {
              const normTitle = normalizeTitle(rec.book_title);
              return !excludedTitles.has(normTitle) && !isBoxSetOrCollection(rec.book_title);
            })
            .map((rec: CachedRecommendation): BookRecommendation => ({
              title: rec.book_title,
              author: rec.book_author || 'Unknown',
              reason: rec.reason || '',
              bookId: rec.book_id || undefined,
              coverUrl: rec.cover_url || undefined,
              confidenceScore: rec.confidence_score || undefined,
              recommendationType: (rec.recommendation_type as BookRecommendation['recommendationType']) || 'safe_bet',
              sourceBooks: rec.source_books || [],
              matchDetails: rec.match_details || undefined,
              sessionId,
            }));

          const classicRec = recommendations.find(r => r.recommendationType === 'classic');
          const regularRecs = recommendations.filter(r => r.recommendationType !== 'classic').slice(0, limit - 1);

          return {
            recommendations: regularRecs,
            classicRecommendation: classicRec || null,
            fromCache: true,
            cacheCount: retryRecs.length - (regularRecs.length + (classicRec ? 1 : 0))
          };
        }
      } catch (retryError) {
        console.log('⏱️ Generation/retry timed out or failed, returning empty state');
      }

      return {
        recommendations: [],
        classicRecommendation: null,
        fromCache: false,
        cacheCount: 0
      };
    },
    staleTime: 1000 * 60 * 1, // 1 minute - shorter for better responsiveness
    gcTime: 1000 * 60 * 30, // 30 minutes
    refetchOnMount: true, // Refetch on mount to get latest
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Refresh function that forces new generation and refetch
  const refresh = async () => {
    if (isRefreshing) return;
    
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Check if user has refreshes remaining
    const canRefresh = await incrementRefreshCount();
    if (!canRefresh) {
      toast.error("Daily refresh limit reached", {
        description: "Refreshes reset at midnight. Try again tomorrow!"
      });
      return;
    }

    setIsRefreshing(true);
    
    try {
      // Invalidate cache first so UI shows loading
      await queryClient.invalidateQueries({ queryKey: ['cachedRecommendations'] });
      
      // Then trigger new generation - manual trigger
      const { error } = await supabase.functions.invoke('generate-cached-recommendations', {
        body: { triggerType: 'manual', targetCount: TARGET_CACHE_COUNT },
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (error) {
        console.error('Manual refresh failed:', error);
        toast.error("Refresh failed", {
          description: "Please try again in a moment."
        });
      } else {
        console.log('✅ Manual refresh triggered');
        // Refetch after a short delay to get new results
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['cachedRecommendations'] });
        }, 2000);
      }
      
      // Also invalidate the fallback recommendations
      await queryClient.invalidateQueries({ queryKey: ['bookRecommendations'] });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Optimistically remove a dismissed recommendation and pull next from cache
  const dismissRecommendation = useCallback(async (bookId: string) => {
    // Optimistically update the local cache to remove the dismissed book
    queryClient.setQueryData(['cachedRecommendations'], (oldData: any) => {
      if (!oldData?.recommendations) return oldData;
      
      // Remove the dismissed recommendation
      const filteredRecs = oldData.recommendations.filter(
        (rec: BookRecommendation) => rec.bookId !== bookId
      );
      
      return {
        ...oldData,
        recommendations: filteredRecs,
      };
    });
    
    // Fetch the next pending recommendation from cache to replace the dismissed one
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    
    // Get currently shown book IDs to exclude
    const currentData = queryClient.getQueryData(['cachedRecommendations']) as any;
    const shownBookIds = new Set(
      (currentData?.recommendations || []).map((r: BookRecommendation) => r.bookId).filter(Boolean)
    );
    
    // Fetch one more pending recommendation that's not already shown
    const { data: nextRecs } = await supabase
      .from('user_cached_recommendations')
      .select('*')
      .eq('user_id', session.user.id)
      .eq('status', 'pending')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false })
      .limit(10); // Get a few extras to find one not already shown
    
    if (nextRecs && nextRecs.length > 0) {
      // Find a rec that's not already displayed
      const nextRec = nextRecs.find((rec: CachedRecommendation) => 
        rec.book_id && !shownBookIds.has(rec.book_id) &&
        rec.recommendation_type !== 'classic' &&
        !rec.book_title?.includes('__')
      );
      
      if (nextRec) {
        const sessionId = crypto.randomUUID();
        const newRecommendation: BookRecommendation = {
          title: nextRec.book_title,
          author: nextRec.book_author || 'Unknown',
          reason: nextRec.reason || '',
          bookId: nextRec.book_id || undefined,
          coverUrl: nextRec.cover_url || undefined,
          confidenceScore: nextRec.confidence_score || undefined,
          recommendationType: (nextRec.recommendation_type as BookRecommendation['recommendationType']) || 'safe_bet',
          sourceBooks: (nextRec.source_books as Array<{ id: string; title: string; reason: string }>) || [],
          matchDetails: nextRec.match_details || undefined,
          sessionId,
        };
        
        // Add the new recommendation to the cache
        queryClient.setQueryData(['cachedRecommendations'], (oldData: any) => {
          if (!oldData) return oldData;
          
          return {
            ...oldData,
            recommendations: [...(oldData.recommendations || []), newRecommendation],
          };
        });
        
        // Mark this one as shown in the database
        supabase
          .from('user_cached_recommendations')
          .update({ status: 'shown', shown_at: new Date().toISOString() })
          .eq('id', nextRec.id)
          .then(({ error }) => {
            if (error) console.error('Failed to mark replacement as shown:', error);
          });
      }
    }
  }, [queryClient]);

  return {
    ...query,
    refresh,
    dismissRecommendation,
    refreshesRemaining,
    isRefreshing,
    dailyLimit: DAILY_REFRESH_LIMIT,
  };
};

async function triggerBackgroundGeneration(accessToken: string, triggerType: string = 'background') {
  try {
    const { error } = await supabase.functions.invoke('generate-cached-recommendations', {
      body: { triggerType, targetCount: TARGET_CACHE_COUNT },
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    
    if (error) {
      console.error('Background generation failed:', error);
    } else {
      console.log(`✅ Background generation triggered (${triggerType})`);
    }
  } catch (e) {
    console.error('Failed to trigger background generation:', e);
  }
}
